import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Menu, X } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

const LANGS = [
  { code: 'fr', label: 'Français', short: 'FR', cc: 'fr' },
  { code: 'en', label: 'English', short: 'EN', cc: 'gb' },
  { code: 'it', label: 'Italiano', short: 'IT', cc: 'it' },
  { code: 'ar', label: 'العربية', short: 'AR', cc: 'ae' },
];
const flagUrl = (cc) => `https://flagcdn.com/40x30/${cc}.png`;

const Navbar = ({ cartItemsCount = 0 }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { lang, t, switchLang } = useLang();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/catalogue', label: t.nav.kits },
    { to: '/galerie', label: t.nav.gallery },
    { to: '/blog', label: t.nav.blog },
    { to: '/contact', label: t.nav.contact },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#050505]/80 border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center">
              <span className="text-white font-heading font-bold text-sm">P</span>
            </div>
            <span className="font-heading font-bold text-white text-lg tracking-tight group-hover:text-purple-400 transition-colors">
              PARTY LED
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-body text-gray-400 hover:text-white transition-colors duration-200 tracking-wide"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 text-gray-300 hover:text-white transition-colors text-sm font-body px-2 py-1 rounded-lg hover:bg-white/5"
                data-testid="lang-switcher"
                aria-label="Changer de langue"
              >
                <img src={flagUrl((LANGS.find((l) => l.code === lang) || LANGS[0]).cc)} alt="" className="w-5 h-auto rounded-[2px] shadow-sm" />
                <span>{(LANGS.find((l) => l.code === lang) || LANGS[0]).short}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 bg-[#0a0a0a] border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[150px] z-50">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { switchLang(l.code); setLangOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-body transition-colors ${
                        lang === l.code
                          ? 'text-purple-400 bg-purple-600/10'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`lang-${l.code}`}
                    >
                      <img src={flagUrl(l.cc)} alt="" className="w-5 h-auto rounded-[2px] shadow-sm" />
                      <span>{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/devis"
              className="hidden md:inline-flex text-sm font-body font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-full px-5 py-2 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.4)]"
              data-testid="nav-cta"
            >
              {t.nav.cta}
            </Link>

            <Link to="/panier" className="relative text-gray-400 hover:text-white transition-colors" data-testid="nav-cart">
              <ShoppingCart className="w-5 h-5" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {cartItemsCount}
                </span>
              )}
            </Link>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-gray-400 hover:text-white transition-colors"
              data-testid="nav-menu-toggle"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden backdrop-blur-xl bg-[#050505]/95 border-t border-white/5">
          <div className="px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMenuOpen(false)}
                className="block text-gray-300 hover:text-white font-body py-3 text-base transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-3 pb-2">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { switchLang(l.code); setIsMenuOpen(false); }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-body transition-colors ${
                    lang === l.code
                      ? 'bg-purple-600 text-white'
                      : 'bg-white/5 text-gray-400 hover:text-white'
                  }`}
                  data-testid={`lang-mobile-${l.code}`}
                >
                  <img src={flagUrl(l.cc)} alt="" className="w-5 h-auto rounded-[2px]" />
                  {l.short}
                </button>
              ))}
            </div>
            <Link
              to="/catalogue"
              onClick={() => setIsMenuOpen(false)}
              className="block text-center font-body font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-full px-5 py-3 mt-2 transition-all"
            >
              {t.nav.cta}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
